import type {Component, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface DropdownStyleSelectorProps extends PropertyProps {
  component?: Component;
  defaultDropDownValue: string;
  dropdownOptions: DropdownListOptions[];
  toggleProps?: string[];
  hideToggledPropsStyleValue?: string;
  onDropDownItemChange?: (selected: Component, property: string, style: string) => void;
}

export interface DropdownListOptions {
  text: string;
  value: string;
  icon?: string;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  styleManager.addType(window.PMW.emails.styles.ID_DROPDOWN_STYLE_SELECTOR, {
    create(data) {
      const {change} = data;
      const props = data.props as DropdownStyleSelectorProps;
      props.component = props.component ?? editor.getSelected();

      const [dropdownContainer, dropdownToggle, dropdownList] = getDropDownStyleSelector(props.dropdownOptions);
      addClickHandlers(props, dropdownToggle, dropdownList, change);
      setInitialValueForDropdown(dropdownContainer, props);

      return dropdownContainer;
    },

    emit(data, styleValue: string) {
      const props = data.props as DropdownStyleSelectorProps;

      if (props.onDropDownItemChange && props.property && props.component) {
        props.onDropDownItemChange(props.component, props.property, styleValue);
        toggleSuboptionsVisibility(props.toggleProps, props.hideToggledPropsStyleValue, styleValue);
      }
    },
  });

  function getDropDownStyleSelector(dropdownOptions: DropdownListOptions[]): HTMLDivElement[] {
    const dropdownSelectorContainer = document.createElement('div');
    dropdownSelectorContainer.className = 'dropdown-selector-container _position-relative flex-justify-end hover-transition-all';

    const dropdown = document.createElement('div');
    dropdown.className = 'dropdown-toggle _cursor-pointer flex-center radius-4 hover-transition-all';
    dropdown.setAttribute('data-value', '');

    const dropdownToggleText = document.createElement('div');
    dropdownToggleText.className = 'dropdown-toggle-text body-md-regular content-body';
    dropdownToggleText.textContent = window.i18next.t('pmwjs_none');

    const dropdownToggleImg = document.createElement('img');
    dropdownToggleImg.className = 'dropdown-toggle-img _hidden';

    const iconUp = document.createElement('i');
    iconUp.className = 'pmw-icon icon-caret-up size-icon-20 content-body spacing-m-l-3 icon-dropdown-open';

    const iconDown = document.createElement('i');
    iconDown.className = 'pmw-icon icon-caret-down size-icon-20 content-body spacing-m-l-3 icon-dropdown-close';

    const dropdownList: HTMLDivElement = getDropdownList(dropdownOptions);

    dropdownSelectorContainer.appendChild(dropdown);
    dropdownSelectorContainer.appendChild(dropdownList);
    dropdown.appendChild(dropdownToggleImg);
    dropdown.appendChild(dropdownToggleText);
    dropdown.appendChild(iconDown);
    dropdown.appendChild(iconUp);

    return [dropdownSelectorContainer, dropdown, dropdownList];
  }

  function getDropdownList(dropdownOptions: DropdownListOptions[]): HTMLDivElement {
    const dropdownList = document.createElement('div');
    dropdownList.className = 'dropdown-list-container _hidden _position-absolute border-s-standard radius-8 bg-neutral-0 spacing-p-3';

    dropdownOptions.forEach((option) => {
      const item = document.createElement('div');
      item.className = 'dropdown-list-item hover-transition-all flexbox flex-content-start flex-items-center _cursor-pointer body-md-regular bg-neutral-0 radius-8 content-body spacing-m-b-2';
      item.textContent = option.text;

      const selectedIcon = document.createElement('i');
      selectedIcon.className = 'pmw-icon icon-check size-icon-20 dropdown-list-icon';
      item.appendChild(selectedIcon);

      if (option.icon) {
        const iconImg = document.createElement('img');
        iconImg.src = option.icon;
        iconImg.className = 'dropdown-list-img hover-transition-all radius-4 bg-neutral-3 spacing-m-r-2';
        item.prepend(iconImg);
      }

      item.setAttribute('data-value', option.value);
      dropdownList.appendChild(item);
    });

    return dropdownList;
  }

  function setInitialValueForDropdown(el: HTMLDivElement, props: DropdownStyleSelectorProps): void {
    const value = opt.editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_STYLE, {component: props.component, property: props.property}) as string;
    const dropdownToggle = el.querySelector('.dropdown-toggle') as HTMLElement;
    const dropdownToggleText = el.querySelector('.dropdown-toggle-text') as HTMLElement;
    const dropdownList = el.querySelector('.dropdown-list-container') as HTMLElement;
    const items = dropdownList?.getElementsByClassName('dropdown-list-item') as HTMLCollectionOf<HTMLElement>;

    const selectedValue = value || props.defaultDropDownValue;

    Array.from(items).forEach((item) => {
      const itemValue = item.getAttribute('data-value');

      if (itemValue === selectedValue) {
        item.classList.add('-selected');

        dropdownToggleText.textContent = item.textContent;
        dropdownToggle.setAttribute('data-value', selectedValue);

        const img = item.querySelector('img.dropdown-list-img');
        const imgSrc = img?.getAttribute('src');

        if (imgSrc) {
          const toggleImg = dropdownToggle.querySelector('.dropdown-toggle-img') as HTMLImageElement;
          toggleImg.className = 'dropdown-toggle-img spacing-m-r-2';
          toggleImg.src = imgSrc;
          toggleImg.classList.add(itemValue);

          dropdownToggle.prepend(toggleImg);
        }

        setInitialAttributesForSuboptionsIfNeeded(el, props.toggleProps, props.hideToggledPropsStyleValue, selectedValue);
      } else {
        item.classList.remove('-selected');
      }
    });
  }

  function addClickHandlers(_props: DropdownStyleSelectorProps, dropdown: HTMLElement, dropdownList: HTMLElement, change: (...args: any) => void): void {
    dropdown.addEventListener('click', () => {
      dropdown.classList.toggle('-open');
      dropdownList.classList.toggle('_hidden');
    });

    const items = dropdownList.getElementsByClassName('dropdown-list-item');

    Array.from(items).forEach((item) => {
      item.addEventListener('click', (e) => {
        const selectedItem = e.target as HTMLElement;

        dropdown.classList.remove('-open');
        dropdownList.classList.add('_hidden');

        Array.from(items).forEach((listItem) => {
          listItem.classList.remove('-selected');
        });

        selectedItem.classList.add('-selected');

        const styleValue = selectedItem.getAttribute('data-value');
        const dropdownToggleText = dropdown.querySelector('.dropdown-toggle-text') as HTMLElement;
        if (dropdownToggleText && styleValue) {
          dropdownToggleText.textContent = selectedItem.textContent;
          dropdown.setAttribute('data-value', styleValue);
        }

        const img = item.querySelector('img.dropdown-list-img');
        const imgSrc = img?.getAttribute('src');

        if (imgSrc && styleValue) {
          const dropdownToggleImg = dropdown.querySelector('.dropdown-toggle-img') as HTMLImageElement;
          dropdownToggleImg.src = imgSrc;
          dropdownToggleImg.className = `dropdown-toggle-img spacing-m-r-2`;
          dropdownToggleImg.classList.add(styleValue);
        }

        change(styleValue);
      });
    });

    document.addEventListener('click', (event) => {
      if (!dropdown.contains(event.target as Node) && !dropdownList.contains(event.target as Node)) {
        dropdown.classList.remove('-open');
        dropdownList.classList.add('_hidden');
      }
    });
  }

  function setInitialAttributesForSuboptionsIfNeeded(el: HTMLElement, properties: string[] | undefined, hideToggledOptionsForStyle: string | undefined, style: string): void {
    if (!properties) {
      return;
    }

    const stringifiedSuboptionProperties = JSON.stringify(properties);
    el.setAttribute('data-suboption-properties', stringifiedSuboptionProperties);

    if (style !== hideToggledOptionsForStyle) {
      el.setAttribute('data-suboption-show', '1');
    } else {
      el.removeAttribute('data-suboption-show');
    }
  }

  function toggleSuboptionsVisibility(properties: string[] | undefined, hideToggledOptionsForStyle: string | undefined, style: string): void {
    if (!properties || !hideToggledOptionsForStyle) {
      return;
    }

    properties.forEach((property) => {
      const propertyContainer = document.querySelector(`.gjs-sm-property__${property}`);

      if (propertyContainer) {
        if (style !== hideToggledOptionsForStyle) {
          propertyContainer.classList.remove('-collapsed');
        } else {
          propertyContainer.classList.add('-collapsed');
        }
      }
    });
  }
};
